<template>
  <div>
    <booking-recovery-notice :lang="lang" @change="bookingRecovery = $event" />
    <aside v-if="careLoadError && !loadError" class="empty" role="alert">
      <p>{{ lang === 'fa' ? 'اطلاعات مسیر برای رزرو جلسهٔ جدید دریافت نشد. جلسه‌های شما همچنان قابل مشاهده‌اند؛ برای رزرو دوباره، وضعیت مسیر را بررسی کنید.' : 'Care information for a new reservation could not be loaded. Your appointments remain available; retry the care lookup before booking again.' }}</p>
      <button type="button" class="btn btn-secondary" :disabled="careLoading" @click="loadCare">{{ lang === 'fa' ? 'بررسی دوبارهٔ مسیر' : 'Retry care lookup' }}</button>
    </aside>
    <div v-if="loadError" class="empty" role="alert"><p>{{ $t('intake.portal.loadError') }}</p><button type="button" class="btn btn-secondary" @click="bootstrap">{{ lang === 'fa' ? 'تلاش دوباره' : 'Try again' }}</button></div>

    <template v-else>
      <div class="section-intro">
        <h2><svg class="icon" aria-hidden="true"><use href="#i-calendar"/></svg> {{ $t('intake.portal.sessionsUpcoming') }}</h2>
        <p>{{ $t('intake.portal.sessionsUpcomingBody') }}</p>
      </div>

      <div v-if="loading" class="empty"><p>{{ $t('intake.portal.loading') }}</p></div>

      <div v-else-if="upcoming.length" class="list">
        <article v-for="session in upcoming" :key="'u-' + session.id" class="list-card">
          <span class="day">{{ formatDay(session) }}</span>
          <div class="copy">
            <h3>{{ formatTitle(session) }}</h3>
            <p>{{ formatMeta(session) }}</p>
          </div>
          <div class="list-actions">
            <router-link class="btn btn-primary btn-sm" :to="detailLink(session)">{{ $t('intake.portal.details') }}</router-link>
            <router-link class="btn btn-secondary btn-sm" :to="rescheduleLink(session)">{{ $t('intake.portal.reschedule') }}</router-link>
          </div>
        </article>
      </div>
      <div v-else class="empty">
        <h3>{{ $t('intake.portal.sessionsNoUpcomingTitle') }}</h3>
        <p>{{ upcomingEmptyBody }}</p>
        <router-link v-if="!bookingRecovery.blocksNewBooking && !careLoading && !careLoadError" class="btn btn-primary" :to="reserveLink">{{ reserveLabel }}</router-link>
      </div>

      <div class="section-intro spaced">
        <h2><svg class="icon" aria-hidden="true"><use href="#i-clock"/></svg> {{ $t('intake.portal.sessionsPast') }}</h2>
      </div>

      <div v-if="past.length" class="list">
        <article v-for="session in past" :key="'p-' + session.id" class="list-card">
          <span class="day">{{ formatDay(session) }}</span>
          <div class="copy">
            <h3>{{ formatTitle(session) }}</h3>
            <p>{{ formatMeta(session) }}</p>
          </div>
          <div class="list-actions">
            <router-link class="btn btn-secondary btn-sm" :to="detailLink(session)">{{ $t('intake.portal.details') }}</router-link>
          </div>
        </article>
      </div>
      <div v-else class="empty">
        <h3>{{ $t('intake.portal.sessionsEmptyTitle') }}</h3>
        <p>{{ $t('intake.portal.sessionsEmptyBody') }}</p>
      </div>
      <nav v-if="!loading && (totalPages > 1 || currentPage > 1)" class="actions session-pagination" :aria-label="lang === 'fa' ? 'صفحه‌بندی سابقهٔ جلسات' : 'Session history pages'">
        <button type="button" class="btn btn-secondary btn-sm" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">{{ lang === 'fa' ? 'صفحهٔ قبل' : 'Previous page' }}</button>
        <span role="status">{{ pageLabel }}</span>
        <button type="button" class="btn btn-secondary btn-sm" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">{{ lang === 'fa' ? 'صفحهٔ بعد' : 'Next page' }}</button>
      </nav>
    </template>
  </div>
</template>
<script>
import BookingRecoveryNotice from '@/components/portal/BookingRecoveryNotice.vue'
import {
  fetchActiveCare,
  fetchSessionPage,
  fetchProfile,
  formatSessionDay,
  formatSessionListTitle,
  formatSessionMeta,
  getCachedProfile,
  isPastSession,
  reserveSessionRoute,
  toFaDigits,
  upcomingFromProfile,
  walletSessionCredit,
} from '@/intake/portalApi'

export default {
  name: 'PortalSessionsStep',
  components: {BookingRecoveryNotice},
  data() {
    return {
      loading: true,
      loadError: false,
      profile: null,
      allSessions: [],
      pagination: {current_page: 1, total_pages: 1},
      episode: null,
      careLoading: false,
      careLoadError: false,
      careGeneration: 0,
      bookingRecovery: {status: 'LOADING', blocksNewBooking: true},
      generation: 0,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    currentPage() {
      const raw = (this.$route.query || {}).page
      const value = Number(Array.isArray(raw) ? raw[0] : raw)
      return Number.isSafeInteger(value) && value > 0 ? value : 1
    },
    totalPages() { return this.pagination.total_pages || 1 },
    pageLabel() {
      return this.lang === 'fa'
        ? `صفحهٔ ${toFaDigits(this.currentPage)} از ${toFaDigits(this.totalPages)}`
        : `Page ${this.currentPage} of ${this.totalPages}`
    },
    upcoming() {
      const fromProfile = upcomingFromProfile(this.profile)
      if (fromProfile.length) return fromProfile
      return this.allSessions.filter((s) => !isPastSession(s))
    },
    past() {
      const upcomingIds = new Set(this.upcoming.map((s) => s.id))
      return this.allSessions
          .filter((s) => isPastSession(s) && !upcomingIds.has(s.id))
          .slice()
    },
    creditCount() {
      return walletSessionCredit(this.profile)
    },
    reserveLink() {
      if (!this.episode) return {name:'intakeScreening', params:{lang:this.lang}}
      if (this.episode.state === 'PREPARING' && this.episode.first_user_session_id) return this.detailLink({id:this.episode.first_user_session_id})
      return reserveSessionRoute(this.lang)
    },
    reserveLabel() {
      if (!this.episode) return this.lang === 'fa' ? 'شروع مسیر و رزرو جلسهٔ اول' : 'Start intake and book your first session'
      if (this.episode.state === 'PREPARING' && this.episode.first_user_session_id) return this.lang === 'fa' ? 'بررسی جلسهٔ اول' : 'Review first session'
      return this.creditCount > 0 ? this.$t('intake.portal.payCreditCta') : this.$t('intake.portal.nextSessionCta')
    },
    upcomingEmptyBody() {
      if (this.creditCount > 0) {
        const display = this.lang === 'fa' ? toFaDigits(this.creditCount) : String(this.creditCount)
        return this.$t('intake.portal.sessionsNoUpcomingBodyCredit', {n: display})
      }
      return this.$t('intake.portal.sessionsNoUpcomingBody')
    },
  },
  mounted() {
    this.bootstrap()
  },
  watch: {'$route.query.page'() { return this.bootstrap() }},
  beforeDestroy() { this.generation += 1; this.careGeneration += 1 },
  methods: {
    r(name) {
      return {name, params: {lang: this.lang, therapistId: 't1'}}
    },
    pageLink(page) {
      const query = {...(this.$route.query || {})}
      if (page > 1) query.page = String(page)
      else delete query.page
      return {name: 'portalSessions', params: {lang: this.lang}, query, hash: this.$route.hash}
    },
    goToPage(page) {
      if (this.loading || page < 1 || page > this.totalPages || page === this.currentPage) return
      return this.$router.push(this.pageLink(page))
    },
    detailLink(session) {
      return {
        name: 'portalSessionDetail',
        params: {lang: this.lang},
        query: session && session.id ? {id: String(session.id)} : {},
      }
    },
    rescheduleLink(session) {
      // Confirmed-session reschedule is a booking-policy screen, not matchmaking slots.
      // Linking to intakeSlots without a V2 match session redirects to screening.
      return {
        name: 'intakeSessionReschedule',
        params: {lang: this.lang},
        query: session && session.id ? {id: String(session.id)} : {},
      }
    },
    formatDay(session) {
      return formatSessionDay(session, this.lang)
    },
    formatTitle(session) {
      return formatSessionListTitle(session, this.lang)
    },
    formatMeta(session) {
      return formatSessionMeta(session, session.doctor, this.lang)
    },
    loadCare() {
      if (this.careLoading) return Promise.resolve()
      const generation = ++this.careGeneration
      this.careLoading = true
      this.careLoadError = false
      this.episode = null
      return fetchActiveCare().then((episode) => {
        if (generation === this.careGeneration) this.episode = episode || null
      }).catch(() => {
        if (generation === this.careGeneration) this.careLoadError = true
      }).finally(() => {
        if (generation === this.careGeneration) this.careLoading = false
      })
    },
    bootstrap() {
      const generation = ++this.generation
      this.loading = true
      this.loadError = false
      this.allSessions = []
      this.loadCare()
      return Promise.all([
        fetchProfile().catch(() => getCachedProfile()),
        fetchSessionPage(this.currentPage),
      ])
          .then(([profile, history]) => {
            if (generation !== this.generation) return
            if (this.currentPage > history.pagination.total_pages) return this.$router.replace(this.pageLink(history.pagination.total_pages))
            this.profile = profile
            this.allSessions = history.sessions
            this.pagination = history.pagination
          })
          .catch(() => {
            if (generation === this.generation) this.loadError = true
          })
          .finally(() => {
            if (generation === this.generation) this.loading = false
          })
    },
  },
}
</script>
<style scoped>
.session-pagination { align-items:center; justify-content:center; margin-top:1.5rem; gap:.75rem }
</style>
